/**
 * Sale results — what an ended sale looks like once the bidding is over.
 *
 * This route used to be a lot grid with a search box: the last bidder page on
 * the pre-2026-09 design, reachable three ways from Sales and looking like the
 * odd one out. Claude Design settled what replaces it (2026-09-08).
 *
 * ROUTING. An open sale has NO landing page — it redirects into Browse filtered
 * to that sale, because a page between the row and the lots would only repeat
 * the row. Only ended sales get a destination of their own, since outcomes are
 * genuinely different content from lots.
 *
 * THE SPINE IS OUTCOME PER LOT, NOT PRICE. Sold, unsold, reserve not met — and
 * the money is one column of that. That is why this is a table and not a price
 * list: sold/unsold, bid counts and lot counts survive every privacy-flag
 * combination, so a house that hides winning amounts gets the same page with
 * one column's worth less information rather than an empty grid.
 *
 * COLOUR. Nothing on an ended sale is live, so nothing here borrows the auction
 * colours. Sold is neutral bold ink; reserve-not-met is a quiet neutral tag and
 * never the red that means outbid, because it is a normal outcome and not a
 * failure. The one exception is the bidder's own won lots, which keep the green
 * they carry everywhere else — that is their standing and it is theirs to see.
 *
 * 🔥 HOT. More than 20 bids. Jim, 2026-09-08, in place of the design's
 * High/Steady/Light interest rating. It is a property of the LOT rather than a
 * substitute for a price, so it sits beside the item and shows in every state —
 * except under `hide_bid_history`, where the counts dash out and the badge must
 * go with them: "Hot" is a bid count above 20 stated in words, and publishing it
 * would leak the number the house just suppressed.
 */
import {
  fetchHouseIdentity,
  fetchLotPhotos,
  fetchMyStandingsForLots,
  houseLabel,
} from '@auctisync/api-client';
import { formatCents, isHotLot, thumbUrl, unsoldReasonLabel, type Tables } from '@auctisync/shared';
import { AUCTION_ROW_COLUMNS, type AuctionRow } from '../../lib/rowColumns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { SaleTermsModal } from '../../components/SaleTermsModal';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';
import { useCatalogNav } from '../preview/CatalogNav';
import { auctionsListPath } from '../preview/previewNav';

type Auction = AuctionRow;

/**
 * Anon-safe lot columns. The six identity columns were revoked from `anon` in
 * 20260720200000, so nothing here names a bidder — which lots the CALLER won is
 * answered separately, from their own bids.
 */
const RESULT_LOT_COLUMNS =
  'id, lot_number, title, status, winning_bid_cents, current_bid_cents, bid_count, has_reserve, reserve_met, no_sale_reason, inventory_item_id';

type ResultLot = Pick<
  Tables<'lots'>,
  | 'id'
  | 'lot_number'
  | 'title'
  | 'status'
  | 'winning_bid_cents'
  | 'current_bid_cents'
  | 'bid_count'
  | 'has_reserve'
  | 'reserve_met'
  | 'no_sale_reason'
  | 'inventory_item_id'
>;

const SOLD_STATUSES = ['sold'];
const OPEN_STATUSES = ['preview', 'active', 'scheduled'];

type OutcomeFilter = 'all' | 'sold' | 'unsold' | 'mine';
type ResultSort = 'lot' | 'bids' | 'result';

function isSold(lot: ResultLot): boolean {
  return SOLD_STATUSES.includes(lot.status);
}

export function PublicAuctionDetail() {
  const { slug } = useParams();
  const { basePath, previewMode } = useCatalogNav();
  const { active, discoverHouse } = useBidderHouses();
  const { userId } = useSession();

  const [auction, setAuction] = useState<Auction | null>(null);
  const [lots, setLots] = useState<ResultLot[]>([]);
  const [photos, setPhotos] = useState<ReadonlyMap<string, string>>(new Map());
  const [wonLotIds, setWonLotIds] = useState<ReadonlySet<string>>(new Set());
  const [openSale, setOpenSale] = useState<{ slug: string; title: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [filter, setFilter] = useState<OutcomeFilter>('all');
  const [sort, setSort] = useState<ResultSort>('lot');

  useEffect(() => {
    if (slug === undefined) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data: auctionData, error: auctionErr } = await supabase
        .from('auctions')
        .select(AUCTION_ROW_COLUMNS)
        .eq('slug', slug)
        .eq('is_visible', true)
        .maybeSingle();
      if (cancelled) return;

      if (auctionErr) {
        setError(auctionErr.message);
        setLoading(false);
        return;
      }
      if (!auctionData) {
        setError('Sale not found.');
        setLoading(false);
        return;
      }
      setAuction(auctionData);

      // Whose sale this is. A visitor arriving from a search result has no
      // house selected, and `tenants` is unreadable to anon — this RPC is the
      // public reader. Discovered is CHROME ONLY, never a membership.
      void fetchHouseIdentity(supabase, auctionData.tenant_id).then((house) => {
        if (!cancelled && house !== null) discoverHouse(house);
      });

      // An open sale never renders this page, so its lots are never fetched.
      if (OPEN_STATUSES.includes(auctionData.status)) {
        setLoading(false);
        return;
      }

      const { data: lotData } = await supabase
        .from('lots')
        .select(RESULT_LOT_COLUMNS)
        .eq('auction_id', auctionData.id)
        .eq('is_visible', true)
        .order('lot_number', { ascending: true });
      if (cancelled) return;
      const rows = (lotData ?? []) as ResultLot[];
      setLots(rows);

      const lotPhotos = await fetchLotPhotos(supabase, rows);
      if (cancelled) return;
      const map = new Map<string, string>();
      for (const lot of rows) {
        const url = lotPhotos.get(lot.id)?.[0]?.url;
        if (url !== undefined) map.set(lot.id, url);
      }
      setPhotos(map);

      // "Another sale is open now" — the one thing a visitor who missed this
      // one can act on. Same house only; a mixed platform list is not an offer.
      const { data: openRows } = await supabase
        .from('auctions')
        .select('slug, title, status, ends_at')
        .eq('tenant_id', auctionData.tenant_id)
        .eq('is_visible', true)
        .in('status', OPEN_STATUSES)
        .order('ends_at', { ascending: true })
        .limit(1);
      if (cancelled) return;
      const next = (openRows ?? [])[0];
      if (next !== undefined) setOpenSale({ slug: next.slug, title: next.title });

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, discoverHouse]);

  /**
   * Which of these lots the CALLER won — asked of the server for the sold lots
   * on this page (`get_my_standings_for_lots`), which compares the crowned
   * winner with the caller itself and answers yes or no. It used to read
   * `lots.winning_user_id` through the caller's bids; a signed-in bidder may
   * not read that column (2026-09-23), so the read failed and no lot was ever
   * marked as theirs. A failure here costs the marks, not the page.
   */
  useEffect(() => {
    const soldIds = lots.filter(isSold).map((l) => l.id);
    if (userId === null || soldIds.length === 0) {
      setWonLotIds(new Set());
      return;
    }
    let cancelled = false;
    void fetchMyStandingsForLots(supabase, soldIds)
      .then((standings) => {
        if (cancelled) return;
        setWonLotIds(new Set([...standings].filter(([, s]) => s.iWon).map(([id]) => id)));
      })
      .catch(() => {
        if (!cancelled) setWonLotIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [userId, lots]);

  const houseName = active === null ? 'the auction house' : houseLabel(active);
  const hideAmounts = auction?.hide_winning_info ?? false;
  const hideCounts = auction?.hide_bid_history ?? false;
  const showUnsold = auction === null ? false : !auction.hide_unsold_lots;

  const soldCount = useMemo(() => lots.filter(isSold).length, [lots]);

  /*
   * 🔴 THE SALE TOTAL IS SUMMED FROM THE LOTS, NOT READ FROM THE SALE.
   *
   * `auctions.total_hammer_cents` exists, looks like the stored aggregate this
   * page wants, and is written by NOTHING — no migration, no worker, no app
   * code, and it is 0 on every auction in production including sales with lots
   * that plainly sold. Rendering it printed "SALE TOTAL $0.00" above three sold
   * lots worth $322.99.
   *
   * Summing the rows is also the better answer on its own terms: the figure a
   * bidder sees is then the sum of the lines they can see, so the page
   * reconciles. `lots` holds every lot — `shown` is only the filtered view — so
   * hiding unsold lots does not change this number.
   *
   * `lot_count` is maintained and is still read; `total_bid_count` is not
   * trustworthy either — see `bidsPlaced` below.
   */
  const hammerTotalCents = useMemo(
    () => lots.reduce((sum, l) => (isSold(l) ? sum + (l.winning_bid_cents ?? 0) : sum), 0),
    [lots],
  );

  /*
   * Bids placed is summed from the lots for the same reason as the total.
   * `auctions.total_bid_count` IS written by something, but it does not agree
   * with the lots: 0 on a staging sale whose rows carry 124 bids between them,
   * and 78 on a production sale whose rows sum to 80. A stat strip that
   * disagrees with the table beneath it is worse than no stat strip, so every
   * number here is the sum of the rows a bidder can count for themselves.
   */
  const bidsPlaced = useMemo(() => lots.reduce((sum, l) => sum + l.bid_count, 0), [lots]);
  const wonHere = useMemo(() => lots.filter((l) => wonLotIds.has(l.id)), [lots, wonLotIds]);

  const shown = useMemo(() => {
    let rows = showUnsold ? lots : lots.filter(isSold);
    if (filter === 'sold') rows = rows.filter(isSold);
    else if (filter === 'unsold') rows = rows.filter((l) => !isSold(l));
    else if (filter === 'mine') rows = rows.filter((l) => wonLotIds.has(l.id));

    const sorted = [...rows];
    if (sort === 'result' && !hideAmounts) {
      sorted.sort((a, b) => (b.winning_bid_cents ?? 0) - (a.winning_bid_cents ?? 0));
    } else if (sort === 'bids' && !hideCounts) {
      sorted.sort((a, b) => b.bid_count - a.bid_count);
    } else {
      sorted.sort((a, b) => a.lot_number - b.lot_number);
    }
    return sorted;
  }, [lots, showUnsold, filter, sort, wonLotIds, hideAmounts, hideCounts]);

  const closeTerms = useCallback(() => {
    setTermsOpen(false);
  }, []);

  if (loading) {
    return (
      <div className="w-wrap">
        <p className="w-body muted" style={{ padding: '40px 0' }}>
          Loading the results…
        </p>
      </div>
    );
  }

  if (error !== null || auction === null) {
    return (
      <div className="w-wrap">
        <div style={{ padding: '40px 0' }}>
          <h1 className="w-h1" style={{ fontSize: 26 }}>
            {error ?? 'Sale not found.'}
          </h1>
          <Link
            to={auctionsListPath(basePath)}
            className="w-btn w-btn-out"
            style={{ marginTop: 18 }}
          >
            All auctions
          </Link>
        </div>
      </div>
    );
  }

  /*
   * An open sale has no page of its own — it goes straight to its lots.
   *
   * The preview surface is the exception: it has no Browse route (a draft
   * tenant reviews a storefront, it does not shop it), so redirecting there
   * would land on nothing. It gets a plain statement instead, which is also
   * the honest answer to an owner previewing a sale that has not run.
   */
  if (OPEN_STATUSES.includes(auction.status)) {
    if (!previewMode) return <Navigate to={`/browse?sale=${auction.slug}`} replace />;
    return (
      <div className="w-wrap">
        <div style={{ padding: '40px 0', maxWidth: 560 }}>
          <div className="w-lbl" style={{ marginBottom: 8 }}>
            Sale results
          </div>
          <h1 className="w-h1" style={{ fontSize: 28 }}>
            {auction.title}
          </h1>
          <p className="w-body muted" style={{ marginTop: 12 }}>
            This sale is still running. Results appear here once every lot has closed — on the live
            site a visitor is taken straight to the lots instead.
          </p>
        </div>
      </div>
    );
  }

  const ran =
    auction.starts_at !== null && auction.ends_at !== null
      ? `Ran ${new Date(auction.starts_at).toLocaleDateString()} – ${new Date(auction.ends_at).toLocaleDateString()}`
      : auction.ends_at !== null
        ? `Ended ${new Date(auction.ends_at).toLocaleDateString()}`
        : null;

  const where = [auction.pickup_city, auction.pickup_region].filter(Boolean).join(', ');
  const nothingSold = soldCount === 0;

  return (
    <div className="w-wrap">
      <nav className="w-crumbs" style={{ paddingTop: 18 }}>
        <Link to={auctionsListPath(basePath)}>Auctions</Link>
        <span aria-hidden>›</span>
        <span>{auction.title}</span>
      </nav>

      <header className="wr-head">
        <div style={{ minWidth: 0 }}>
          <div className="w-lbl" style={{ marginBottom: 8 }}>
            Sale results
          </div>
          <h1 className="w-h1" style={{ fontSize: 34 }}>
            {auction.title}
          </h1>
          {auction.preview_text !== null && auction.preview_text.trim() !== '' ? (
            <p className="w-body muted" style={{ marginTop: 12, maxWidth: 560 }}>
              {auction.preview_text}
            </p>
          ) : null}
          <div className="w-sale-m" style={{ marginTop: 18 }}>
            {ran !== null ? (
              <div>
                <span className="w-lbl">Dates</span>
                <b style={{ fontSize: 15 }}>{ran.replace(/^(Ran|Ended) /, '')}</b>
              </div>
            ) : null}
            {where !== '' ? (
              <div>
                <span className="w-lbl">Collected at</span>
                <b style={{ fontSize: 15 }}>{where}</b>
              </div>
            ) : null}
            <div>
              <span className="w-lbl">Reference</span>
              <b style={{ fontSize: 15 }}>
                {auction.reference_number === null ? '—' : String(auction.reference_number)}
              </b>
            </div>
          </div>
        </div>

        {/*
         * Three audiences arrive here and this answers exactly ONE of them: a
         * bidder who won (their invoice), a bidder who did not and a signed-out
         * visitor from a search engine (the sale that is open now). This is the
         * most search-indexable page on the site, so the signed-out state is a
         * first-class state, not a fallback.
         */}
        <aside className="wr-aside">
          {wonHere.length > 0 ? (
            <>
              <span className="tag t-win">
                You won {wonHere.length} {wonHere.length === 1 ? 'lot' : 'lots'}
              </span>
              <p className="w-body">
                {hideAmounts
                  ? 'Your invoice has the amounts and how to pay.'
                  : `${formatCents(
                      wonHere.reduce((sum, l) => sum + (l.winning_bid_cents ?? 0), 0),
                    )} before premium and tax. Your invoice has the total and how to pay.`}
              </p>
              <Link to="/my/invoices" className="w-btn w-btn-cta w-btn-full">
                View your invoice<span aria-hidden>›</span>
              </Link>
            </>
          ) : openSale !== null ? (
            <>
              <span className="w-lbl">Open now</span>
              <p className="w-body">
                <b>{openSale.title}</b> is taking bids at {houseName}.
              </p>
              <Link to={`/browse?sale=${openSale.slug}`} className="w-btn w-btn-cta w-btn-full">
                Browse that sale<span aria-hidden>›</span>
              </Link>
            </>
          ) : (
            <>
              <span className="w-lbl">This sale has ended</span>
              <p className="w-body">
                {houseName} has no sale open right now. New sales appear on the Sales page.
              </p>
              <Link to={auctionsListPath(basePath)} className="w-btn w-btn-out w-btn-full">
                All auctions
              </Link>
            </>
          )}
          <button
            type="button"
            className="w-btn w-btn-out w-btn-sm"
            onClick={() => {
              setTermsOpen(true);
            }}
          >
            Sale terms
          </button>
        </aside>
      </header>

      <div className="wr-stats">
        <div className="wr-stat">
          <span className="w-lbl">Lots</span>
          <b>{auction.lot_count > 0 ? auction.lot_count : lots.length}</b>
        </div>
        {/* A count is not a trail, so bid counts survive `hide_winning_info` —
            but not `hide_bid_history`, which is the flag that hides counts. */}
        {hideCounts ? null : (
          <div className="wr-stat">
            <span className="w-lbl">Bids placed</span>
            <b>{bidsPlaced.toLocaleString()}</b>
          </div>
        )}
        <div className="wr-stat">
          <span className="w-lbl">Sold</span>
          <b>
            {soldCount} <span style={{ fontSize: 15, fontWeight: 500 }}>of {lots.length}</span>
          </b>
        </div>
        {/* When amounts are hidden the sale total goes with them, and the slot
            becomes unsold — `lots.length − sold`, both of which we hold. An
            invented aggregate in this slot would be implemented as a real
            column by somebody. */}
        {hideAmounts ? (
          <div className="wr-stat">
            <span className="w-lbl">Unsold</span>
            <b>{lots.length - soldCount}</b>
          </div>
        ) : (
          <div className="wr-stat">
            <span className="w-lbl">Sale total</span>
            <b>{formatCents(hammerTotalCents)}</b>
          </div>
        )}
      </div>

      {hideAmounts || hideCounts || !showUnsold ? (
        <p className="wr-note">
          <span aria-hidden>ⓘ</span>
          <span>
            {[
              hideAmounts ? 'does not publish what lots sold for' : null,
              hideCounts ? 'does not publish bid counts' : null,
              !showUnsold ? 'lists only the lots that sold' : null,
            ]
              .filter((s): s is string => s !== null)
              .reduce(
                (acc, part, i, arr) =>
                  i === 0
                    ? `${houseName} ${part}`
                    : i === arr.length - 1
                      ? `${acc} and ${part}`
                      : `${acc}, ${part}`,
                '',
              )}
            . Your own results are always shown to you.
          </span>
        </p>
      ) : null}

      {lots.length === 0 ? (
        <p className="w-body muted" style={{ padding: '32px 0' }}>
          This sale has no published lots.
        </p>
      ) : (
        <>
          <div className="w-toolbar" style={{ marginTop: 26 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* With unsold hidden there is nothing to filter, so the filter
                  goes. A control with one option is furniture. */}
              {showUnsold
                ? (
                    [
                      ['all', 'All outcomes'],
                      ['sold', 'Sold'],
                      ['unsold', 'Unsold'],
                      ...(wonHere.length > 0 ? [['mine', 'Your lots']] : []),
                    ] as [OutcomeFilter, string][]
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      className="w-chip"
                      aria-pressed={filter === id}
                      onClick={() => {
                        setFilter(id);
                      }}
                    >
                      {label}
                    </button>
                  ))
                : null}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="w-lbl">Sort</span>
              <select
                className="w-sel"
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value as ResultSort);
                }}
              >
                <option value="lot">Lot number</option>
                {hideCounts ? null : <option value="bids">Most bids</option>}
                {hideAmounts ? null : <option value="result">Highest result</option>}
              </select>
            </label>
          </div>

          <h2 className="w-h2" style={{ fontSize: 21, margin: '22px 0 12px' }}>
            Results
            <span className="muted" style={{ fontSize: 14, fontWeight: 500, marginLeft: 10 }}>
              {shown.length} of {lots.length} lots
            </span>
          </h2>

          <div className="wr-tblwrap">
            <table className="wr-tbl">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>Lot</th>
                  <th>Item</th>
                  <th style={{ width: 150 }}>Outcome</th>
                  {hideCounts ? null : <th style={{ width: 74 }}>Bids</th>}
                  {hideAmounts ? null : <th style={{ width: 132, textAlign: 'right' }}>Result</th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((lot) => {
                  const sold = isSold(lot);
                  const mine = wonLotIds.has(lot.id);
                  const hot = isHotLot(lot.bid_count, hideCounts);
                  const thumb = photos.get(lot.id);
                  return (
                    <tr key={lot.id}>
                      <td className="wl-num mono">{lot.lot_number}</td>
                      <td>
                        <div className="wr-item">
                          {thumb === undefined ? (
                            <div className="ph wr-thumb" aria-hidden />
                          ) : (
                            <img className="wr-thumb" src={thumbUrl(thumb)} alt="" loading="lazy" />
                          )}
                          <div style={{ minWidth: 0 }}>
                            <div className="wr-t">{lot.title}</div>
                          </div>
                          {hot ? (
                            <span className="wr-hot" title="More than 20 bids">
                              🔥 Hot
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td data-l="Outcome">
                        {mine ? (
                          <span className="tag t-win">You won this</span>
                        ) : sold ? (
                          <span style={{ fontWeight: 600 }}>Sold</span>
                        ) : (
                          <span className="tag t-quiet">
                            {unsoldReasonLabel({
                              noSaleReason: lot.no_sale_reason,
                              // Both are nullable. A null reserve is no
                              // reserve; a null `reserve_met` is "not met",
                              // never "met" — the permissive reading would
                              // claim a lot cleared a bar nobody recorded.
                              hasReserve: lot.has_reserve ?? false,
                              reserveMet: lot.reserve_met ?? false,
                              bidCount: lot.bid_count,
                            })}
                          </span>
                        )}
                      </td>
                      {hideCounts ? null : (
                        <td data-l="Bids" className="mono">
                          {lot.bid_count}
                        </td>
                      )}
                      {hideAmounts ? null : (
                        <td data-l="Result" className="wr-r">
                          {sold && lot.winning_bid_cents !== null ? (
                            <>
                              {formatCents(lot.winning_bid_cents)}
                              {mine ? (
                                <span className="w-lbl" style={{ display: 'block', marginTop: 3 }}>
                                  Your bid
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Reserve-not-met is a normal outcome and must never read as failure.
              When it is the WHOLE sale, saying so plainly is the difference
              between a record and an apology. */}
          {nothingSold ? (
            <p className="wr-note" style={{ marginTop: 16 }}>
              <span aria-hidden>ⓘ</span>
              <span>
                Nothing in this sale met its reserve. Unsold lots are often relisted, sometimes
                without one — they usually reappear in a later sale.
              </span>
            </p>
          ) : null}
        </>
      )}

      <section className="wr-tcard" style={{ margin: '34px 0 48px' }}>
        <span className="w-lbl">Sale terms</span>
        <b>What this sale was sold under</b>
        <p className="w-body muted">
          The premium, what was required before bidding, how lots closed, and where lots were
          collected. These are the terms the invoices from this sale were struck under.
        </p>
        <button
          type="button"
          className="w-btn w-btn-out"
          style={{ marginTop: 14 }}
          onClick={() => {
            setTermsOpen(true);
          }}
        >
          Read the sale terms
        </button>
      </section>

      {termsOpen ? (
        <SaleTermsModal
          auction={auction}
          houseName={houseName}
          onClose={closeTerms}
          browseTo={null}
        />
      ) : null}
    </div>
  );
}
